import { fetchIncomeStatement, IncomeStatement, fetchBalanceSheet, BalanceSheet, fetchCashFlowStatement, CashFlowStatement, fetchHistoricalPrices, fetchIncomeStatementTTMSeries, fetchCashFlowStatementTTMSeries } from '../services/stockApi';
import { fetchFiscalStockPrices } from '../services/fiscalApi';
import { config } from '../config/config';

const AUTH_STORAGE_KEY = 'garp_admin_auth_v1';

function hasBackendAuthToken(): boolean {
  try {
    if (typeof window === 'undefined') return false;
    const raw = window.localStorage?.getItem(AUTH_STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { token?: unknown; expiresAtUtc?: unknown };
    const token = typeof parsed?.token === 'string' ? parsed.token.trim() : '';
    if (!token) return false;

    const expiresAtUtc = typeof parsed?.expiresAtUtc === 'string' ? parsed.expiresAtUtc : '';
    const expiresMs = expiresAtUtc ? new Date(expiresAtUtc).getTime() : NaN;
    if (Number.isFinite(expiresMs) && expiresMs <= Date.now()) return false;

    return true;
  } catch {
    return false;
  }
}

function isApiEnabled(): boolean {
  if (!config.useRealAPI) return false;

  // API mode via authenticated backend proxy only.
  const backendBase = typeof config.backend?.baseUrl === 'string' ? config.backend.baseUrl.trim() : '';
  return backendBase.length > 0 && hasBackendAuthToken();
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const waitForBackendAuthToken = async (timeoutMs: number): Promise<boolean> => {
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    if (hasBackendAuthToken()) return true;
    await sleep(200);
  }
  return hasBackendAuthToken();
};

const getHttpStatusFromError = (error: unknown): number => {
  const raw = Number((error as any)?.status ?? (error as any)?.response?.status);
  return Number.isFinite(raw) ? raw : NaN;
};

// API-only: store API data per symbol
let apiIncomeStatementsCache: Map<string, { annual: IncomeStatement[], quarterly: IncomeStatement[] }> = new Map();
let apiIncomeStatementTTMSeriesCache: Map<string, IncomeStatement[]> = new Map();
let apiBalanceSheetsCache: Map<string, { annual: BalanceSheet[], quarterly: BalanceSheet[] }> = new Map();
let apiCashFlowStatementsCache: Map<string, { annual: CashFlowStatement[], quarterly: CashFlowStatement[] }> = new Map();
let apiCashFlowStatementTTMSeriesCache: Map<string, CashFlowStatement[]> = new Map();
let apiPricesCache: Map<string, any[]> = new Map(); // Store price data per symbol
const DEFAULT_SYMBOL = config.api?.defaultSymbol || 'AAPL';
let currentSymbol: string = DEFAULT_SYMBOL;
// Cache for generated stock data (typed after StockSymbol is defined)
let _mockStockData: any = null;
let _lastSymbol: string | null = null;

// Get API data for specific symbol
function getApiIncomeStatements(symbol: string = DEFAULT_SYMBOL) {
  return apiIncomeStatementsCache.get(symbol) || null;
}

function getApiBalanceSheets(symbol: string = DEFAULT_SYMBOL) {
  return apiBalanceSheetsCache.get(symbol) || null;
}

function getApiCashFlowStatements(symbol: string = DEFAULT_SYMBOL) {
  return apiCashFlowStatementsCache.get(symbol) || null;
}

function getApiIncomeStatementTTMSeries(symbol: string = DEFAULT_SYMBOL) {
  return apiIncomeStatementTTMSeriesCache.get(symbol) || null;
}

export type StockLoadFailureReason = 'no-auth' | 'no-prices' | 'api-failed';

export type StockLoadResult = {
  ok: boolean;
  reason?: StockLoadFailureReason;
};

// Load data function
export async function loadStockData(
  symbol: string = DEFAULT_SYMBOL,
  authToken?: string | null,
  authExpiresAtUtc?: string | null
): Promise<StockLoadResult> {
  console.log('🔄 loadStockData called for', symbol, 'useRealAPI:', config.useRealAPI);
  if (authToken) {
    try {
      const raw = window.localStorage?.getItem(AUTH_STORAGE_KEY);
      const stored = raw ? JSON.parse(raw) as { expiresAtUtc?: unknown } : {};
      const nextExpiresAtUtc =
        typeof authExpiresAtUtc === 'string' && authExpiresAtUtc.trim().length > 0
          ? authExpiresAtUtc
          : (typeof stored?.expiresAtUtc === 'string' ? stored.expiresAtUtc : undefined);
      window.localStorage?.setItem(AUTH_STORAGE_KEY, JSON.stringify({
        ...(stored || {}),
        token: authToken,
        expiresAtUtc: nextExpiresAtUtc,
      }));
    } catch {
      // The API layer will use its existing storage fallback if storage is unavailable.
    }
  }
  // Reset cache if symbol changed
  if (currentSymbol !== symbol) {
    _mockStockData = null;
    _lastSymbol = null;
  }
  
  currentSymbol = symbol;
  
  let apiEnabled = isApiEnabled();
  if (!apiEnabled) {
    // After login the token may arrive slightly later than the first data load.
    const tokenReady = await waitForBackendAuthToken(2500);
    apiEnabled = tokenReady && isApiEnabled();
  }

  if (apiEnabled) {
    console.log('📡 Using API...');
    // Always load fresh data from API (don't use cache to get latest prices)
    const maxAttempts = 3;
    let lastFailure: StockLoadFailureReason = 'api-failed';

    const fetchPricesWithRetry = async (): Promise<any[]> => {
      const priceAttempts = 3;
      for (let i = 1; i <= priceAttempts; i += 1) {
        try {
          const prices = await fetchHistoricalPrices(symbol);
          if (Array.isArray(prices) && prices.length > 0) return prices;
          if (i < priceAttempts) {
            await sleep(600 * i);
          }
        } catch (error) {
          const status = getHttpStatusFromError(error);
          if (status === 401 || status === 403) {
            throw error;
          }
          if (i < priceAttempts) {
            await sleep(600 * i);
          }
        }
      }

      // Fallback 1: keep chart usable when FMP prices are temporarily empty after relogin.
      try {
        const fiscalRows = await fetchFiscalStockPrices(symbol);
        if (Array.isArray(fiscalRows) && fiscalRows.length > 0) {
          const mapped = fiscalRows
            .map((row: any) => ({
              date: String(row?.date || ''),
              close: Number(row?.close_price),
              price: Number(row?.close_price),
              open: Number(row?.open_price),
              high: Number(row?.high_price),
              low: Number(row?.low_price),
              volume: Number(row?.volume),
            }))
            .filter((row) => Number.isFinite(new Date(row.date).getTime()) && Number.isFinite(row.close))
            .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

          if (mapped.length > 0) {
            console.warn(`Charts price fallback: using Fiscal prices for ${symbol} (${mapped.length} points).`);
            return mapped;
          }
        }
      } catch {
        // ignore and try cache fallback
      }

      // Fallback 2: use existing in-memory cache (if available) instead of failing hard.
      const cachedPrices = apiPricesCache.get(symbol);
      if (Array.isArray(cachedPrices) && cachedPrices.length > 0) {
        console.warn(`Charts price fallback: using cached prices for ${symbol} (${cachedPrices.length} points).`);
        return cachedPrices;
      }

      return [];
    };

    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      try {
        const [annualIncome, quarterlyIncome, annualBalance, quarterlyBalance, annualCashFlow, quarterlyCashFlow] = await Promise.all([
          fetchIncomeStatement(symbol, 'annual', 15),
          fetchIncomeStatement(symbol, 'quarter', 60), // 15 years * 4 quarters
          fetchBalanceSheet(symbol, 'annual', 15),
          fetchBalanceSheet(symbol, 'quarter', 60),
          fetchCashFlowStatement(symbol, 'annual', 15),
          fetchCashFlowStatement(symbol, 'quarter', 60)
        ]);

        const prices = await fetchPricesWithRetry();

        if (!prices || prices.length === 0) {
          lastFailure = 'no-prices';
          throw new Error('Loaded 0 price points');
        }

        apiIncomeStatementsCache.set(symbol, { annual: annualIncome, quarterly: quarterlyIncome });
        apiBalanceSheetsCache.set(symbol, { annual: annualBalance, quarterly: quarterlyBalance });
        apiCashFlowStatementsCache.set(symbol, { annual: annualCashFlow, quarterly: quarterlyCashFlow });
        apiPricesCache.set(symbol, prices);

        // Optional (but preferred): load TTM series as-is for charts that need TTM without quarterly summation.
        // Do not fail the whole load if these endpoints error.
        const [incomeTtmRes, cashTtmRes] = await Promise.allSettled([
          fetchIncomeStatementTTMSeries(symbol, 120),
          fetchCashFlowStatementTTMSeries(symbol, 120),
        ]);
        if (incomeTtmRes.status === 'fulfilled') {
          apiIncomeStatementTTMSeriesCache.set(symbol, incomeTtmRes.value);
        }
        if (cashTtmRes.status === 'fulfilled') {
          apiCashFlowStatementTTMSeriesCache.set(symbol, cashTtmRes.value);
        }

        console.log(`✅ Loaded ${prices.length} price points from API for ${symbol}. Last date:`, prices[prices.length - 1]?.date);

        // Force regeneration even if symbol didn't change, because we just refreshed API caches.
        _mockStockData = null;
        _lastSymbol = null;
        return { ok: true };
      } catch (error) {
        const status = getHttpStatusFromError(error);
        if (status === 401 || status === 403) {
          lastFailure = 'no-auth';
          break;
        }
        if (attempt < maxAttempts) {
          console.warn(`⚠️ Error loading API financial data for ${symbol} (attempt ${attempt}/${maxAttempts})`, error);
        } else {
          console.error(`❌ Error loading API financial data for ${symbol} (attempt ${attempt}/${maxAttempts}):`, error);
        }
        const msg = error instanceof Error ? error.message : String(error ?? '');
        if (msg.toLowerCase().includes('loaded 0 price points')) {
          lastFailure = 'no-prices';
        }
        if (attempt < maxAttempts) {
          await sleep(500 * attempt);
        }
      }
    }
    return { ok: false, reason: lastFailure };

  }

  console.error('❌ API-only mode: missing FMP API key and backend proxy is not available. Provide REACT_APP_FMP_API_KEY or login so the app can use the backend proxy.');
  return { ok: false, reason: 'no-auth' };
}

export interface StockDataPoint {
  timestamp: Date;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

export interface FinancialDataPoint {
  timestamp: Date;
  revenue: number; // in billions USD
  netIncome: number; // in billions USD
  eps: number; // earnings per share
  operatingIncome: number; // in billions USD
  grossProfit: number; // in billions USD
  pretaxIncome: number; // in billions USD
}

export interface StockSymbol {
  symbol: string;
  name: string;
  data: StockDataPoint[];
  financialData?: FinancialDataPoint[];
}

// Historical price data points (loaded from JSON or API)
function getHistoricalPrices(symbol: string = 'AAPL') {
  // If using API, get from cache
  const apiEnabled = isApiEnabled();
  if (apiEnabled) {
    const prices = apiPricesCache.get(symbol);
    if (!prices) return [];
    console.log(`💰 getHistoricalPrices from API cache: ${prices.length} prices. Last:`, prices[prices.length - 1]);
    return prices.map((item: any) => ({
      date: item.date,
      open: item.open,
      high: item.high,
      low: item.low,
      close: item.close ?? item.price,
      volume: item.volume,
    }));
  }

  // API-only: no local JSON fallback.
  return [];
}

// Get specific financial metric data from API or JSON
export function getFinancialMetricData(
  metric: 'revenue' | 'netIncome' | 'eps' | 'operatingIncome' | 'grossProfit' | 'pretaxIncome',
  category: 'Annual Data' | 'Quarterly Data' = 'Annual Data',
  symbol: string = 'AAPL'
): number[] {
  console.log(`🔍 getFinancialMetricData: metric=${metric}, category=${category}, useRealAPI=${config.useRealAPI}`);
  
  // Check if using API data
  const apiEnabled = isApiEnabled();
  if (apiEnabled) {
    const apiIncomeStatements = getApiIncomeStatements(symbol);
    if (!apiIncomeStatements) {
      return [];
    }
    
    const statements = category === 'Annual Data' 
      ? apiIncomeStatements.annual 
      : apiIncomeStatements.quarterly;
    
    // Map the metric to API field
    const metricMapping: Record<string, string> = {
      revenue: 'revenue',
      netIncome: 'netIncome',
      eps: 'epsDiluted',
      operatingIncome: 'operatingIncome',
      grossProfit: 'grossProfit',
      pretaxIncome: 'incomeBeforeTax'
    };
    
    const apiField = metricMapping[metric];
    
    // Extract values - API returns values in full numbers
    const result = statements
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .map((statement: IncomeStatement) => {
        const value = (statement as any)[apiField];
        // EPS is already in correct units, don't divide
        // Other metrics are in full USD, convert to millions
        return metric === 'eps' ? value : value / 1000000;
      });
    
    return result;
  }

  // API-only: no local JSON fallback.
  return [];
}

// Get Balance Sheet metric data from API only (no JSON support yet)
export function getBalanceSheetMetricData(
  metric: 'cashAndCashEquivalents' | 'totalAssets' | 'shortTermDebt' | 'longTermDebt' | 'totalLiabilities' | 'totalStockholdersEquity',
  category: 'Annual Data' | 'Quarterly Data' = 'Annual Data',
  symbol: string = 'AAPL'
): number[] {
  const apiEnabled = isApiEnabled();

  // Only API support for now
  if (!apiEnabled) return [];
  
  const apiBalanceSheets = getApiBalanceSheets(symbol);
  if (!apiBalanceSheets) {
    return [];
  }
  
  const statements = category === 'Annual Data' 
    ? apiBalanceSheets.annual 
    : apiBalanceSheets.quarterly;
  
  // Map the metric to API field
  const metricMapping: Record<string, string> = {
    cashAndCashEquivalents: 'cashAndCashEquivalents',
    totalAssets: 'totalAssets',
    shortTermDebt: 'shortTermDebt',
    longTermDebt: 'longTermDebt',
    totalLiabilities: 'totalLiabilities',
    totalStockholdersEquity: 'totalStockholdersEquity'
  };
  
  const apiField = metricMapping[metric];
  
  // Extract values - API returns values in full numbers
  const result = statements
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .map((statement: BalanceSheet) => {
      const value = (statement as any)[apiField];
      // Convert to millions to match other financial data format
      return value / 1000000;
    });
  
  return result;
}

// Get Cash Flow Statement metric data from API only (no JSON support yet)
export function getCashFlowMetricData(
  metric: 'operatingCashFlow' | 'freeCashFlow' | 'dividendsPaid' | 'stockRepurchased',
  category: 'Annual Data' | 'Quarterly Data' = 'Annual Data',
  symbol: string = 'AAPL'
): number[] {
  const apiEnabled = isApiEnabled();

  // Only API support for now
  if (!apiEnabled) return [];
  
  const apiCashFlowStatements = getApiCashFlowStatements(symbol);
  if (!apiCashFlowStatements) {
    return [];
  }
  
  const statements = category === 'Annual Data' 
    ? apiCashFlowStatements.annual 
    : apiCashFlowStatements.quarterly;
  
  // Map the metric to API field
  const metricMapping: Record<string, string> = {
    operatingCashFlow: 'operatingCashFlow',
    freeCashFlow: 'freeCashFlow',
    dividendsPaid: 'dividendsPaid',
    stockRepurchased: 'commonStockRepurchased'
  };
  
  const apiField = metricMapping[metric];
  
  // Extract values - API returns values in full numbers
  const result = statements
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .map((statement: CashFlowStatement) => {
      let value = (statement as any)[apiField];
      
      // For dividends, try alternative fields if primary is not available
      if (metric === 'dividendsPaid' && (value === undefined || value === null || isNaN(value))) {
        // Try commonDividendsPaid first (most common)
        value = (statement as any)['commonDividendsPaid'];
        if (value === undefined || value === null || isNaN(value)) {
          // Try netDividendsPaid as fallback
          value = (statement as any)['netDividendsPaid'];
        }
      }
      
      // Handle undefined/null values and convert to millions
      if (value === undefined || value === null || isNaN(value)) {
        return 0; // Default to 0 for missing data
      }
      // Convert to millions and make positive (dividends are negative in cash flow)
      return Math.abs(value) / 1000000;
    });
  
  return result;
}

// Get periods (full dates) for the financial data
export function getFinancialDataPeriods(category: 'Annual Data' | 'Quarterly Data' = 'Annual Data', symbol: string = 'AAPL'): Date[] {
  // Check if using API data
  const apiEnabled = isApiEnabled();
  if (apiEnabled) {
    const apiIncomeStatements = getApiIncomeStatements(symbol);
    if (!apiIncomeStatements) {
      return [];
    }
    
    const statements = category === 'Annual Data' 
      ? apiIncomeStatements.annual 
      : apiIncomeStatements.quarterly;
    
    const dates = statements
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .map(statement => new Date(statement.date));
    return dates;
  }

  // API-only: no local JSON fallback.
  return [];
}

// Get years for the financial data (for backward compatibility)
export function getFinancialDataYears(category: 'Annual Data' | 'Quarterly Data' = 'Annual Data', symbol: string = 'AAPL'): number[] {
  const dates = getFinancialDataPeriods(category, symbol);
  const years = dates.map(date => date.getFullYear());
  return years;
}

function generateAnnualFinancialData(symbol: string): FinancialDataPoint[] {
  const years = getFinancialDataYears('Annual Data', symbol);
  const revenue = getFinancialMetricData('revenue', 'Annual Data', symbol);
  const netIncome = getFinancialMetricData('netIncome', 'Annual Data', symbol);
  const eps = getFinancialMetricData('eps', 'Annual Data', symbol);
  const operatingIncome = getFinancialMetricData('operatingIncome', 'Annual Data', symbol);
  const grossProfit = getFinancialMetricData('grossProfit', 'Annual Data', symbol);
  const pretaxIncome = getFinancialMetricData('pretaxIncome', 'Annual Data', symbol);
  
  return years.map((year, index) => ({
    timestamp: new Date(year, 11, 31), // December 31st of each year
    revenue: revenue[index] || 0,
    netIncome: netIncome[index] || 0,
    eps: eps[index] || 0,
    operatingIncome: operatingIncome[index] || 0,
    grossProfit: grossProfit[index] || 0,
    pretaxIncome: pretaxIncome[index] || 0
  }));
}

// Generate realistic AAPL data based on historical prices
function generateHistoricalData(symbol: string = 'AAPL'): StockDataPoint[] {
  const data: StockDataPoint[] = [];
  
  // Convert real historical OHLCV data directly to StockDataPoint format
  const historicalPrices = getHistoricalPrices(symbol);
  console.log(`📊 generateHistoricalData: Got ${historicalPrices.length} prices. Last:`, historicalPrices[historicalPrices.length - 1]);
  historicalPrices.forEach((point: any) => {
    const date = new Date(point.date);
    const close = typeof point.close === 'number' ? point.close : Number(point.close);
    if (!Number.isFinite(close)) return;

    const open = (typeof point.open === 'number' && Number.isFinite(point.open)) ? point.open : close;
    const high = (typeof point.high === 'number' && Number.isFinite(point.high)) ? point.high : Math.max(open, close);
    const low = (typeof point.low === 'number' && Number.isFinite(point.low)) ? point.low : Math.min(open, close);
    const volume = (typeof point.volume === 'number' && Number.isFinite(point.volume)) ? point.volume : 0;
    
    data.push({
      timestamp: date,
      open: Math.max(open, 0.01),
      high: Math.max(high, 0.01),
      low: Math.max(low, 0.01),
      close: Math.max(close, 0.01),
      volume
    });
  });
  
  // Sort by date to ensure chronological order
  return data.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
}

// Calculate PE (Price-to-Earnings) ratio based on trailing 4 quarters EPS
export function calculatePE(symbol: string = 'AAPL'): { date: Date; value: number }[] {
  const priceData = generateHistoricalData(symbol);
  
  // Check if using API data
  const apiEnabled = isApiEnabled();
  if (apiEnabled) {
    const ttmSeriesRaw = getApiIncomeStatementTTMSeries(symbol);
    const ttmSeries = Array.isArray(ttmSeriesRaw) ? ttmSeriesRaw : [];
    if (!ttmSeries || ttmSeries.length === 0) {
      return [];
    }

    const ttmPoints = ttmSeries
      .map((row: IncomeStatement) => ({
        date: new Date(row.date),
        eps: row.epsDiluted
      }))
      .filter(item => Number.isFinite(item.date.getTime()) && Number.isFinite(item.eps))
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    if (ttmPoints.length === 0) return [];
    
    const peData: { date: Date; value: number }[] = [];
    let ttmIndex = 0;
    let latestEps: number | null = null;
    
    // For each price point, calculate PE
    for (const pricePoint of priceData) {
      const priceDate = new Date(pricePoint.timestamp);

      // Use latest AS-IS TTM EPS point at or before this price date.
      while (ttmIndex < ttmPoints.length && ttmPoints[ttmIndex].date.getTime() <= priceDate.getTime()) {
        latestEps = ttmPoints[ttmIndex].eps;
        ttmIndex += 1;
      }
      if (latestEps == null) continue;

      // Business rule: PE cannot be negative. When EPS is <= 0, PE is forced to 0.
      const peRaw = latestEps > 0 ? (pricePoint.close / latestEps) : 0;
      const pe = Number.isFinite(peRaw) && peRaw > 0 ? peRaw : 0;
      peData.push({ date: priceDate, value: pe });
    }
    return peData;
  }

  // API-only: no local JSON fallback.
  return [];
}

// Calculate Fair Price - EPS × Average P/E (5-year historical average)
export function calculateFairPrice(symbol: string = 'AAPL'): { date: Date; value: number }[] {
  // Get quarterly EPS data
  const epsData = getFinancialMetricData('eps', 'Quarterly Data', symbol);
  const periods = getFinancialDataPeriods('Quarterly Data', symbol);
  
  // Get price data to calculate historical P/E
  const apiEnabled = isApiEnabled();
  if (!apiEnabled) return [];
  const priceData: any[] = apiPricesCache.get(symbol) || [];
  
  if (epsData.length < 4 || periods.length < 4 || priceData.length === 0) {
    return [];
  }
  
  // Calculate TTM EPS for each quarter
  const ttmEPSData: { date: Date; eps: number; price: number }[] = [];
  for (let i = 3; i < epsData.length; i++) {
    const last4Quarters = epsData.slice(i - 3, i + 1);
    const epsSum = last4Quarters.reduce((sum, eps) => sum + eps, 0);
    const quarterEndDate = periods[i];
    
    // Find the closest price to this quarter end date
    const closestPrice = priceData.reduce((closest, current) => {
      const currentDate = new Date(current.date);
      const closestDate = new Date(closest.date);
      const diffCurrent = Math.abs(currentDate.getTime() - quarterEndDate.getTime());
      const diffClosest = Math.abs(closestDate.getTime() - quarterEndDate.getTime());
      return diffCurrent < diffClosest ? current : closest;
    });
    
    if (epsSum > 0) {
      ttmEPSData.push({
        date: quarterEndDate,
        eps: epsSum,
        price: closestPrice.close
      });
    }
  }
  
  if (ttmEPSData.length === 0) {
    return [];
  }
  
  // Calculate P/E for each point
  const peData = ttmEPSData.map(item => ({
    date: item.date,
    pe: item.price / item.eps
  })).filter(item => item.pe > 0 && item.pe < 100); // Filter out unreasonable P/E values
  
  // Calculate 5-year average P/E (or all available data if less than 5 years)
  const fiveYearsAgo = new Date();
  fiveYearsAgo.setFullYear(fiveYearsAgo.getFullYear() - 5);
  
  const recentPEData = peData.filter(item => item.date >= fiveYearsAgo);
  const avgPE = recentPEData.length > 0
    ? recentPEData.reduce((sum, item) => sum + item.pe, 0) / recentPEData.length
    : peData.reduce((sum, item) => sum + item.pe, 0) / peData.length;
  
  console.log('📊 Fair Price Calculation:', {
    avgPE: avgPE.toFixed(2),
    dataPoints: recentPEData.length,
    lastEPS: ttmEPSData[ttmEPSData.length - 1]?.eps.toFixed(2)
  });
  
  // Calculate Fair Price for each quarter: Fair Price = EPS × Average P/E
  const fairPriceData = ttmEPSData.map(item => ({
    date: item.date,
    value: item.eps * avgPE
  }));
  
  // Return only last 4 quarters (matching flat-eps-ttm display)
  return fairPriceData.slice(-4);
}

// Calculate Fair Price Flat EPS (TTM) - Flat EPS (TTM) × Average P/E (5-year historical average)
// If API doesn't provide a dedicated metric, compute it from available EPS + price data.
export function calculateFairPriceFlatEPSTTM(
  symbol: string = 'AAPL',
  dateMode: FinancialDateMode = 'as-reported'
): { date: Date; value: number }[] {
  const flatEPSTTM = calculateFlatEPSTTM(symbol, dateMode);
  if (flatEPSTTM.length === 0) {
    return [];
  }

  const peSeries = calculatePE(symbol);
  if (peSeries.length === 0) {
    return [];
  }

  const fiveYearsAgo = new Date();
  fiveYearsAgo.setFullYear(fiveYearsAgo.getFullYear() - 5);

  const filtered = peSeries
    .filter(p => p.value > 0 && p.value < 100)
    .filter(p => p.date >= fiveYearsAgo);

  const base = filtered.length > 0
    ? filtered
    : peSeries.filter(p => p.value > 0 && p.value < 100);

  if (base.length === 0) {
    return [];
  }

  const avgPE = base.reduce((sum, p) => sum + p.value, 0) / base.length;

  return flatEPSTTM.map(point => ({
    date: point.date,
    value: point.value * avgPE,
  }));
}

// Calculate Flat EPS (TTM) - trailing 4 quarters sum, displayed as horizontal steps
export type FinancialDateMode = 'as-reported' | 'at-reported';

export function resolveFmpAtReportedDate(
  symbol: string,
  periodEnd: Date,
  acceptedDate?: unknown,
  filingDate?: unknown
): Date {
  const parseDate = (value: unknown): Date | null => {
    if (typeof value !== 'string' || !value.trim()) return null;
    const date = new Date(value.includes(' ') ? value.replace(' ', 'T') : value);
    return Number.isNaN(date.getTime()) ? null : date;
  };

  const reportedDate = parseDate(acceptedDate) || parseDate(filingDate);
  if (reportedDate && reportedDate.getTime() > periodEnd.getTime()) return reportedDate;

  // LVMUY's FMP rows contain period-end dates instead of an earnings date.
  // Keep this provider-specific fallback isolated from symbols with valid dates.
  if (symbol.toUpperCase() === 'LVMUY') {
    return new Date(periodEnd.getTime() + 30 * 86400000);
  }

  return reportedDate || periodEnd;
}

// as-reported: align to fiscal period end (API: row.date)
// at-reported: align to filing date (API: row.filingDate)
export function calculateFlatEPSTTM(
  symbol: string = 'AAPL',
  dateMode: FinancialDateMode = 'as-reported'
): { date: Date; value: number }[] {
  // Check if using API data
  const apiEnabled = isApiEnabled();
  if (apiEnabled) {
    const income = getApiIncomeStatements(symbol);
    const quarterly = income?.quarterly || [];

    const parseFmpDate = (raw: unknown): Date | null => {
      if (typeof raw !== 'string') return null;
      const s = raw.trim();
      if (!s) return null;

      // FMP sometimes returns "YYYY-MM-DD HH:mm:ss". Try a local-time ISO first,
      // then fall back to UTC. This avoids runtime-dependent parsing quirks.
      if (s.includes(' ') && !s.includes('T')) {
        const asLocalIso = s.replace(' ', 'T');
        const dtLocal = new Date(asLocalIso);
        if (!Number.isNaN(dtLocal.getTime())) return dtLocal;

        const asUtcIso = `${asLocalIso}Z`;
        const dtUtc = new Date(asUtcIso);
        if (!Number.isNaN(dtUtc.getTime())) return dtUtc;
      }

      const dt = new Date(s);
      return Number.isNaN(dt.getTime()) ? null : dt;
    };

    // Compute TTM EPS from quarterly EPS diluted (rolling 4 quarters).
    // This fixes cases where the provider's income-statement-ttm series is missing
    // the latest quarter endpoint.
    const computedFromQuarterly = (() => {
      if (!Array.isArray(quarterly) || quarterly.length < 4) return [] as { date: Date; value: number; periodEnd: Date }[];

      const rows = quarterly
        .map((row: any) => {
          const periodEndRaw = row?.date;
          const epsRaw = row?.epsDiluted ?? row?.eps;
          const epsParsed = typeof epsRaw === 'number'
            ? epsRaw
            : (typeof epsRaw === 'string' ? Number.parseFloat(epsRaw) : null);

          const derivedEps = (() => {
            if (typeof epsParsed === 'number' && Number.isFinite(epsParsed)) return epsParsed;
            const netIncome = row?.netIncome;
            const sharesDil = row?.weightedAverageShsOutDil;
            if (typeof netIncome !== 'number' || !Number.isFinite(netIncome)) return null;
            if (typeof sharesDil !== 'number' || !Number.isFinite(sharesDil) || sharesDil <= 0) return null;
            const v = netIncome / sharesDil;
            return Number.isFinite(v) ? v : null;
          })();

          const eps = derivedEps;
          const periodEnd = (() => {
            const dt = new Date(periodEndRaw);
            if (!Number.isNaN(dt.getTime())) return dt;
            const parsed = parseFmpDate(periodEndRaw);
            return parsed || new Date(periodEndRaw);
          })();

          // at-reported anchors to filingDate.
          const reportDate = resolveFmpAtReportedDate(symbol, periodEnd, row?.acceptedDate, row?.filingDate);
          return {
            periodEnd,
            reportDate,
            eps: typeof eps === 'number' ? eps : null,
          };
        })
        .filter((r) => !Number.isNaN(r.periodEnd.getTime()))
        .sort((a, b) => a.periodEnd.getTime() - b.periodEnd.getTime());

      const out: { date: Date; value: number; periodEnd: Date }[] = [];
      for (let i = 3; i < rows.length; i++) {
        let sum = 0;
        let ok = true;
        for (let j = i - 3; j <= i; j++) {
          const v = rows[j].eps;
          if (typeof v !== 'number' || !Number.isFinite(v)) {
            ok = false;
            break;
          }
          sum += v;
        }
        if (!ok || !Number.isFinite(sum)) continue;

        const periodEnd = rows[i].periodEnd;
        const dt = dateMode === 'at-reported'
          ? (rows[i].reportDate || periodEnd)
          : periodEnd;

        out.push({ date: dt, value: sum, periodEnd });
      }
      return out;
    })();

    const ttmSeriesRaw = getApiIncomeStatementTTMSeries(symbol);
    const ttmSeries: IncomeStatement[] = Array.isArray(ttmSeriesRaw) ? ttmSeriesRaw : [];

    // Build a stable quarter-keyed series so as-reported and at-reported use the same VALUES
    // per quarter, only differing by X alignment (period end vs filing date).
    const periodEndToReportDate = (() => {
      const map = new Map<string, string>();
      if (!Array.isArray(quarterly) || quarterly.length === 0) return map;
      for (const row of quarterly) {
        const periodEnd = (row as any)?.date;
        const filing = (row as any)?.acceptedDate || (row as any)?.filingDate;
        if (typeof periodEnd === 'string' && periodEnd && typeof filing === 'string' && filing) {
          map.set(periodEnd.slice(0, 10), filing);
        }
      }
      return map;
    })();

    const quarterlyPeriodEndsSorted = (() => {
      const items: { key: string; ms: number }[] = [];
      for (const row of quarterly as any[]) {
        const raw = row?.date;
        if (typeof raw !== 'string' || raw.length < 10) continue;
        const key = raw.slice(0, 10);
        const dt = parseFmpDate(raw) ?? new Date(raw);
        const ms = dt.getTime();
        if (!Number.isFinite(ms)) continue;
        items.push({ key, ms });
      }
      items.sort((a, b) => a.ms - b.ms);
      // Deduplicate keys
      const out: { key: string; ms: number }[] = [];
      const seen = new Set<string>();
      for (const it of items) {
        if (seen.has(it.key)) continue;
        seen.add(it.key);
        out.push(it);
      }
      return out;
    })();

    const computedByPeriodEnd = new Map<string, number>();
    // Some symbols are not truly quarterly (e.g., semi-annual cadence).
    // In those cases summing 4 "quarterly" points inflates TTM, so only
    // trust computed TTM when cadence is close to a real quarter.
    const isQuarterLikeCadence = (() => {
      if (!Array.isArray(quarterlyPeriodEndsSorted) || quarterlyPeriodEndsSorted.length < 6) return false;
      const diffsDays: number[] = [];
      for (let i = 1; i < quarterlyPeriodEndsSorted.length; i++) {
        const dMs = quarterlyPeriodEndsSorted[i].ms - quarterlyPeriodEndsSorted[i - 1].ms;
        if (!Number.isFinite(dMs) || dMs <= 0) continue;
        diffsDays.push(dMs / 86400000);
      }
      if (diffsDays.length < 4) return false;
      const sortedDiffs = [...diffsDays].sort((a, b) => a - b);
      const median = sortedDiffs[Math.floor(sortedDiffs.length / 2)];
      // Accept normal quarter ranges and mild calendar drift.
      return median >= 70 && median <= 110;
    })();

    if (isQuarterLikeCadence) {
      for (const row of computedFromQuarterly) {
        const key = row.periodEnd.toISOString().slice(0, 10);
        if (Number.isFinite(row.value) && row.value !== 0) {
          computedByPeriodEnd.set(key, row.value);
        }
      }
    }

    // For provider TTM series: map each row to a *fiscal* period end.
    // Some companies (e.g., AAPL) use non-calendar quarter ends (52/53-week fiscal year),
    // so calendar-quarter mapping can mis-assign TTM points and make values appear to
    // "change" when switching to TTM.
    const getPeriodEndKeyForProviderRow = (reportOrPeriodDate: Date, rawDateKey: string | null): string | null => {
      if (rawDateKey) {
        // If provider supplies a date that matches a known fiscal period end, use it.
        if (quarterlyPeriodEndsSorted.some((q) => q.key === rawDateKey)) return rawDateKey;
      }

      if (quarterlyPeriodEndsSorted.length === 0) return null;
      const ms = reportOrPeriodDate.getTime();
      if (!Number.isFinite(ms)) return null;

      // Choose the latest fiscal period end <= report date.
      // This aligns filing dates back to their correct fiscal quarter.
      let chosen = quarterlyPeriodEndsSorted[0];
      for (const q of quarterlyPeriodEndsSorted) {
        if (q.ms <= ms) chosen = q;
        else break;
      }
      return chosen?.key ?? null;
    };

    // Build provider map: periodEndKey -> { value, reportDate }
    const providerByPeriodEnd = new Map<string, { value: number; reportDate: Date }>();
    for (const row of ttmSeries) {
      const rawPeriodOrReportDate = typeof row?.date === 'string' ? row.date : '';
      if (!rawPeriodOrReportDate) continue;
      const v = (row as any)?.epsDiluted;
      if (typeof v !== 'number' || !Number.isFinite(v) || v === 0) continue;

      // at-reported anchors to filingDate.
      const periodEnd = parseFmpDate(rawPeriodOrReportDate) ?? new Date(rawPeriodOrReportDate);
      const reportDate = resolveFmpAtReportedDate(symbol, periodEnd, row?.acceptedDate, row?.filingDate);
      if (Number.isNaN(reportDate.getTime())) continue;

      const rawKey = rawPeriodOrReportDate.slice(0, 10);
      const periodEndKey = getPeriodEndKeyForProviderRow(reportDate, rawKey);
      if (!periodEndKey) continue;
      providerByPeriodEnd.set(periodEndKey, { value: v, reportDate });
    }

    const keys = Array.from(new Set([
      ...Array.from(computedByPeriodEnd.keys()),
      ...Array.from(providerByPeriodEnd.keys()),
    ]))
      .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

    if (keys.length === 0) return [];

    return keys
      .map((k) => {
        const computedVal = computedByPeriodEnd.get(k);
        const providerEntry = providerByPeriodEnd.get(k);
        const value = computedVal ?? providerEntry?.value ?? 0;

        const date = (() => {
          if (dateMode === 'at-reported') {
            // Use the report date from provider if available, else from quarterly filing map
            if (providerEntry?.reportDate) return providerEntry.reportDate;
            const raw = periodEndToReportDate.get(k) ?? k;
            return parseFmpDate(raw) ?? new Date(k);
          }
          // as-reported: use period end directly
          return new Date(k);
        })();
        return { date, value };
      })
      .filter((p): p is { date: Date; value: number } => typeof p.value === 'number' && Number.isFinite(p.value) && p.value > 0 && !Number.isNaN(p.date.getTime()))
      .sort((a, b) => a.date.getTime() - b.date.getTime());
  }

  // API-only: no local JSON fallback.
  return [];
}

function generateCurrentSymbolData(): StockSymbol[] {
  return [
    {
      symbol: currentSymbol,
      name: currentSymbol,
      data: generateHistoricalData(currentSymbol),
      financialData: generateAnnualFinancialData(currentSymbol),
    },
  ];
}

// Lazy-loaded stock data (defined at top of file)

export const mockStockData: StockSymbol[] = new Proxy([] as StockSymbol[], {
  get(target, prop) {
    // Generate data on first access if loaded
    // For API mode: need apiIncomeStatements + prices for current symbol
    const apiEnabled = isApiEnabled();
    const dataLoaded = apiEnabled && !!getApiIncomeStatements(currentSymbol) && apiPricesCache.has(currentSymbol);
    
    // Regenerate if symbol changed
    if (_lastSymbol !== currentSymbol) {
      _mockStockData = null;
      _lastSymbol = currentSymbol;
    }
    
    if (!_mockStockData && dataLoaded) {
      _mockStockData = generateCurrentSymbolData();
    }
    
    // Return from cached data
    if (_mockStockData) {
      return (_mockStockData as any)[prop];
    }
    
    // Return empty array properties if data not loaded yet
    return ([] as any)[prop];
  }
});



